import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MouseGlow } from '@/components/site/MouseGlow';
import { MagneticButton } from '@/components/site/MagneticButton';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { CTABand } from '@/components/site/CTABand';
import { SITE, SERVICES, WORK, STATS, CLIENTS } from '@/data/site';

gsap.registerPlugin(ScrollTrigger);

export default function Home() {
  const pinRef = useRef(null);
  const stagesRef = useRef(null);
  const heroRef = useRef(null);
  const parallaxRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    const ctx = gsap.context(() => {
      // Signature animation: pinned services scroller
      if (pinRef.current && stagesRef.current) {
        const stages = gsap.utils.toArray(stagesRef.current.children);
        const total = stages.length;
        // Fraction of a card's segment reserved for the card-to-card
        // transition. The remaining (1 - TRANSITION) portion is a clean
        // hold where the card sits fully in place with no motion. The
        // transition itself is split into two SEQUENTIAL halves — the
        // outgoing card fully exits (autoAlpha 0) before the incoming
        // card starts entering — so there is never a moment where both
        // cards are simultaneously visible.
        const TRANSITION = 0.2;
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: pinRef.current,
            start: 'top top',
            end: () => `+=${total * 70}%`,
            pin: true,
            scrub: 0.6,
            anticipatePin: 1,
          },
        });
        stages.forEach((stage, i) => {
          if (i === 0) return;
          const exitStart = i - TRANSITION;
          const enterStart = i - TRANSITION / 2;
          tl.to(
            stages[i - 1],
            { yPercent: -100, autoAlpha: 0, duration: TRANSITION / 2, ease: 'power2.in' },
            exitStart
          );
          tl.fromTo(
            stage,
            { yPercent: 100, autoAlpha: 0 },
            { yPercent: 0, autoAlpha: 1, duration: TRANSITION / 2, ease: 'power2.out' },
            enterStart
          );
        });
        // Pad the timeline so the last card also gets a full hold instead of
        // unpinning the instant its entrance finishes.
        tl.set({}, {}, total);
      }

      // Hero parallax decorative element (scroll-linked)
      if (parallaxRef.current) {
        gsap.to(parallaxRef.current, {
          yPercent: -30,
          ease: 'none',
          scrollTrigger: {
            trigger: heroRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
          },
        });
      }
    });

    return () => ctx.revert();
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative overflow-hidden min-h-[100svh] flex flex-col justify-end"
      >
        <MouseGlow intensity={0.32} />
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        {/* parallax mark */}
        <div
          ref={parallaxRef}
          aria-hidden
          className="pointer-events-none absolute -right-20 top-24 w-[60vw] max-w-[800px] aspect-square opacity-[0.07]"
        >
          <svg viewBox="0 0 200 200" className="w-full h-full">
            <defs>
              <linearGradient id="bigDelta" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#6C5CE7" />
                <stop offset="100%" stopColor="#4B9FFF" />
              </linearGradient>
            </defs>
            <path d="M100 14 L186 178 L14 178 Z" fill="none" stroke="url(#bigDelta)" strokeWidth="0.6" />
            <path d="M100 30 L172 168 L28 168 Z" fill="none" stroke="url(#bigDelta)" strokeWidth="0.6" />
            <path d="M100 46 L158 158 L42 158 Z" fill="none" stroke="url(#bigDelta)" strokeWidth="0.6" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24 w-full">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-12">
              <SectionLabel num="00" data-testid="hero-eyebrow">
                Web Studio · est. 2017 · India
              </SectionLabel>
            </div>
            <div className="md:col-span-11">
              <WordReveal
                as="h1"
                text="We design and ship digital products that feel inevitable."
                accent="inevitable"
                data-testid="hero-headline"
                className="mt-10 text-[44px] sm:text-6xl md:text-7xl lg:text-[84px] font-extrabold tracking-tightest leading-[1.02] text-balance"
              />
            </div>
            <div className="md:col-span-7 md:col-start-6">
              <Reveal delay={600}>
                <p className="mt-10 text-lg md:text-xl text-ink-muted leading-relaxed max-w-xl">
                  A twelve-person studio out of India building marketing sites, web apps, mobile apps,
                  custom internal tools, ecommerce, and AI features. We work small, ship often, and stay
                  uncomfortably close to the work.
                </p>
              </Reveal>
              <Reveal delay={760}>
                <div className="mt-10 flex flex-wrap items-center gap-3">
                  <MagneticButton
                    href={SITE.whatsapp}
                    external
                    variant="primary"
                    data-testid="hero-cta-primary"
                  >
                    Book discovery call
                    <ArrowUpRight size={16} />
                  </MagneticButton>
                  <MagneticButton to="/work" variant="ghost" data-testid="hero-cta-secondary">
                    See selected work
                  </MagneticButton>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Stats strip — asymmetric */}
          <div className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10 max-w-5xl">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 80}>
                <div className="border-l border-white/[0.12] pl-5">
                  <div className="text-3xl md:text-4xl font-bold tracking-tight tabular-nums">
                    {s.kpi}
                    <span className="text-violet">{s.suffix}</span>
                  </div>
                  <div className="mt-1.5 text-[11px] uppercase tracking-[0.22em] text-ink-muted">
                    {s.label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="absolute bottom-6 left-6 md:left-12 text-[11px] uppercase tracking-[0.28em] text-ink-muted/70 hidden md:flex items-center gap-3">
          <span className="h-px w-8 bg-white/15" /> scroll
        </div>
      </section>

      {/* MARQUEE — clients */}
      <section data-testid="clients-marquee" className="relative py-10 border-y border-white/10 overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-r from-ink-bg to-transparent pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-l from-ink-bg to-transparent pointer-events-none" />
        <div className="flex marquee-track whitespace-nowrap gap-12 text-ink-muted">
          {[...CLIENTS, ...CLIENTS].map((c, i) => (
            <span key={i} className="text-base md:text-lg tracking-tight font-medium">
              <span className="text-white/85">{c}</span>
              <span className="mx-12 text-ink-muted/40">/</span>
            </span>
          ))}
        </div>
      </section>

      {/* WHAT WE DO — pinned signature */}
      <section ref={pinRef} data-testid="services-pinned" className="relative h-[100svh] overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-start pt-24">
          <div className="md:col-span-4 md:sticky md:top-32">
            <SectionLabel num="01">What we do</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1] text-balance">
              Six services, one
              <br />
              <span className="italic text-violet">opinionated</span> studio.
            </h2>
            <p className="mt-5 text-ink-muted leading-relaxed max-w-sm">
              We don’t do everything. We do these six things, and we do them with the kind of obsession that doesn’t scale—and that’s the point.
            </p>
            <Link
              to="/services"
              className="mt-8 arrow-link inline-flex items-center gap-2 text-sm text-white"
              data-testid="services-pinned-cta"
            >
              All services <span className="arrow">→</span>
            </Link>
          </div>

          <div className="md:col-span-8 relative h-[60vh] md:h-[75vh]">
            <div ref={stagesRef} className="absolute inset-0">
              {SERVICES.map((s, i) => (
                <div
                  key={s.slug}
                  data-testid={`pinned-service-${s.slug}`}
                  className="absolute inset-0 flex flex-col justify-end"
                  style={{ opacity: i === 0 ? 1 : 0 }}
                >
                  <div className="rounded-[20px] border border-white/[0.12] bg-ink-surface/70 backdrop-blur-sm p-8 md:p-10">
                    <div className="flex items-baseline justify-between gap-6">
                      <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">
                        {s.num} / 06
                      </div>
                      <div
                        className="h-1.5 w-24 rounded-full"
                        style={{ background: `linear-gradient(90deg, ${s.accent}, transparent)` }}
                      />
                    </div>
                    <h3 className="mt-8 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.05]">
                      {s.title}
                    </h3>
                    <p className="mt-5 text-ink-muted max-w-xl leading-relaxed">{s.long}</p>
                    <div className="mt-7 flex flex-wrap gap-2">
                      {s.deliverables.map((d) => (
                        <span key={d} className="text-xs px-3 py-1.5 rounded-full border border-white/[0.12] text-white/85">
                          {d}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED WORK */}
      <section data-testid="featured-work" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between gap-6 mb-14">
            <div>
              <SectionLabel num="02">Selected work</SectionLabel>
              <h2 className="mt-6 text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.05] max-w-2xl text-balance">
                Real names. Real <span className="italic text-electric">numbers.</span>
              </h2>
            </div>
            <Link to="/work" className="arrow-link hidden md:inline-flex items-center gap-2 text-sm text-white">
              View all <span className="arrow">→</span>
            </Link>
          </div>

          {/* Asymmetric grid: 3 cards spanning 7/5/12 */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {WORK.slice(0, 3).map((w, i) => {
              const spans = ['md:col-span-7 md:row-span-2', 'md:col-span-5', 'md:col-span-5'];
              const heights = ['min-h-[460px] md:min-h-[640px]', 'min-h-[300px] md:min-h-[300px]', 'min-h-[300px] md:min-h-[316px]'];
              return (
                <Reveal key={w.slug} delay={i * 90} className={`${spans[i]}`}>
                  <Link
                    to={`/work#${w.slug}`}
                    data-testid={`featured-work-${w.slug}`}
                    className={`group relative block h-full ${heights[i]} rounded-[20px] border border-white/[0.12] bg-ink-surface overflow-hidden p-8 md:p-10 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-white/25 hover:shadow-[0_30px_80px_-30px_rgba(108,92,231,0.45)]`}
                  >
                    {/* Decorative number / color block */}
                    <div
                      className="absolute -right-20 -top-20 h-72 w-72 rounded-full opacity-30 blur-3xl transition-opacity duration-500 group-hover:opacity-60"
                      style={{ background: w.palette[0] }}
                    />
                    <div className="relative flex h-full flex-col">
                      <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.24em] text-ink-muted">
                        <span>{w.sector}</span>
                        <span className="tabular-nums">{w.year}</span>
                      </div>
                      <h3 className="mt-auto text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
                        {w.name}
                      </h3>
                      <p className="mt-3 text-ink-muted max-w-md leading-relaxed text-sm md:text-base">
                        {w.summary}
                      </p>
                      <div className="mt-7 flex items-end justify-between">
                        <div>
                          <div className="text-3xl md:text-4xl font-bold tracking-tight tabular-nums" style={{ color: w.palette[0] }}>
                            {w.stat.kpi}
                          </div>
                          <div className="text-[11px] uppercase tracking-[0.22em] text-ink-muted mt-1">
                            {w.stat.label}
                          </div>
                        </div>
                        <div className="arrow-link text-sm text-white inline-flex items-center gap-2">
                          Case study <span className="arrow">→</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section data-testid="philosophy" className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="03">How we work</SectionLabel>
            <div className="mt-6 inline-flex items-center gap-2 text-violet text-sm">
              <Sparkles size={16} /> Small team, big surface area
            </div>
          </div>
          <div className="md:col-span-8">
            <Reveal>
              <p className="text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight leading-[1.25] text-balance">
                We embed with founders and product owners for{' '}
                <span className="text-violet">twelve focused weeks</span>—not twelve scattered months.
                Designers and engineers work in the same file, on the same Slack, against the same brief.
                The work is the deliverable; the deck is a side-effect.
              </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
              {[
                { k: '12', v: 'weeks per engagement' },
                { k: '3', v: 'partners we say yes to / qtr' },
                { k: '0', v: 'subcontracted work' },
              ].map((it, i) => (
                <Reveal key={it.v} delay={i * 90}>
                  <div className="border-t border-white/[0.12] pt-5">
                    <div className="text-4xl md:text-5xl font-bold tracking-tight tabular-nums text-white">
                      {it.k}
                    </div>
                    <div className="mt-2 text-sm text-ink-muted leading-snug">{it.v}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
